import { radius as radiusLookup } from "@bluehive/zipcodes";
import { ExpandMore } from "@mui/icons-material";
import {
  InputAdornment,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { uniq } from "lodash";
import { ReactElement, useEffect } from "react";
import {
  Path,
  UseControllerProps,
  useFormContext,
  useWatch,
  FieldValues,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme, { palette } from "~frontend/theme";
import { plural } from "~frontend/util/plural";

import { RhfCommaSeparatedTextField } from "./RhfCommaSeparatedTextField";
import { RhfTextField } from "./RhfTextField";

export interface RhfZipCodeRadiusProps<
  TFormData extends FieldValues = FieldValues,
> {
  control: UseControllerProps<TFormData>["control"];
  centerZipsName: Path<TFormData>;
  radiusName: Path<TFormData>;
  valuesName: Path<TFormData>;
  fullWidth?: boolean;
  bottomLabel?: string;
}
export function RhfZipCodeRadius<TFormData extends FieldValues = FieldValues>({
  control,
  centerZipsName,
  radiusName,
  valuesName,
  fullWidth = true,
  bottomLabel,
}: RhfZipCodeRadiusProps<TFormData>): ReactElement {
  const { setValue } = useFormContext<TFormData>();
  const zipCodes = useWatch({
    control,
    name: centerZipsName,
  }) as string[] | undefined;

  const radius = useWatch({
    control,
    name: radiusName,
  });

  const lookupResults = useWatch({
    control,
    name: valuesName,
  }) as string[] | undefined;

  useEffect(() => {
    if (Array.isArray(zipCodes)) {
      const results = zipCodes.reduce((acc, zip) => {
        const zips = radiusLookup(zip, Number(radius));
        return [...acc, ...zips];
      }, [] as string[]);

      setValue(valuesName, uniq(results).sort() as any);
    }
  }, [zipCodes, radius, valuesName, setValue]);

  return (
    <FlexBox direction="column">
      <FlexBox gap={2}>
        <RhfCommaSeparatedTextField
          label="Zip Code"
          control={control}
          name={centerZipsName}
          fullWidth={fullWidth}
        />
        <RhfTextField
          label="Radius"
          control={control}
          name={radiusName}
          fullWidth={fullWidth}
          slotProps={{
            htmlInput: {
              step: 5,
              min: 0,
            },
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ paddingRight: "5px" }}>
                  <Typography component={"span"}>miles</Typography>
                </InputAdornment>
              ),
            },
          }}
        />
      </FlexBox>
      {bottomLabel && (
        <Typography variant="caption" color="textSecondary">
          {bottomLabel}
        </Typography>
      )}
      {radius &&
        Array.isArray(zipCodes) &&
        zipCodes.length > 0 &&
        Array.isArray(lookupResults) && (
          <Accordion
            square={true}
            sx={{
              marginTop: theme.spacing(1),
              padding: 0,
              color: "darkGray",
              "& .MuiAccordionSummary-root": { minHeight: "auto" },
              "&.Mui-expanded": { margin: theme.spacing(1, 0, 0, 0) },
              "& .MuiAccordionSummary-content": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ fontSize: "15px" }} />}
              sx={{
                padding: theme.spacing(0, 0, 1, 0),
                borderBottom: "1px solid",
                borderBottomColor: `${palette.darkGray}88`,
              }}
            >
              <Typography component={"span"} variant="body3Bold">
                {plural(lookupResults.length, "zip code", "zip codes")} within{" "}
                {plural(Number(radius), "mile", "miles")} of{" "}
                {zipCodes.join(", ")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RhfTextField
                control={control}
                name={valuesName}
                fullWidth={true}
                multiline={true}
                minRows={2}
                sx={{
                  paddingTop: 0,
                  "& .MuiInputBase-root": { border: "none" },
                }}
                slotProps={{
                  input: {
                    readOnly: true,
                    sx: {
                      "& .MuiInputBase-readOnly": {
                        background: palette.lightGray,
                        padding: 0,
                      },
                    },
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        )}
    </FlexBox>
  );
}
